import { currentAuth } from "../auth/auth";
import { toggleMode, themeIcon, currentMode } from "../core/appTheme";

const LOGO = `<svg class="brand-logo" viewBox="0 0 32 32" width="34" height="34"><rect width="32" height="32" rx="8"/><path d="M7 21l5-7 4 3 9-10" stroke-width="2.6" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

interface ToolDef {
  id: string;
  name: string;
  sub: string;
  icon: string;
  mount: (host: HTMLElement) => void;
}

// Registry — new tools just get pushed here in the future.
const TOOLS: ToolDef[] = [
  {
    id: "average",
    name: "Averaging",
    sub: "Live average of any numbers",
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20L20 4"/><path d="M4 8h5"/><path d="M15 16h5"/></svg>`,
    mount: mountAveraging,
  },
];

export function showTools() {
  const app = document.getElementById("app")!;
  app.innerHTML = `
    <div class="tools-wrap">
      <div class="home-top">
        <div class="auth-brand">${LOGO}<div><div class="auth-title">Tools</div><div class="auth-sub">Handy trading utilities</div></div></div>
        <div style="display:flex;gap:8px;align-items:center">
          <button class="mini-btn icon-btn" id="tools-theme" title="Toggle light / dark">${themeIcon(currentMode())}</button>
          <a class="mini-btn" href="/">← Home</a>
        </div>
      </div>
      <div class="tools-layout">
        <div class="tools-nav" id="tools-nav"></div>
        <div class="tools-content" id="tools-content"></div>
      </div>
    </div>`;

  const nav = app.querySelector("#tools-nav")!;
  const content = app.querySelector<HTMLElement>("#tools-content")!;

  const themeBtn = app.querySelector<HTMLElement>("#tools-theme")!;
  themeBtn.addEventListener("click", () => {
    toggleMode();
    themeBtn.innerHTML = themeIcon(currentMode());
  });

  const select = (t: ToolDef) => {
    nav.querySelectorAll(".tools-nav-item").forEach((n) => n.classList.toggle("active", (n as HTMLElement).dataset.id === t.id));
    content.innerHTML = "";
    t.mount(content);
  };

  for (const t of TOOLS) {
    const b = document.createElement("button");
    b.className = "tools-nav-item";
    b.dataset.id = t.id;
    b.innerHTML = `<span class="tni-icon">${t.icon}</span><span class="tni-text"><span class="tni-name">${t.name}</span><span class="tni-sub">${t.sub}</span></span>`;
    b.addEventListener("click", () => select(t));
    nav.appendChild(b);
  }

  select(TOOLS[0]);
  // touch currentAuth so tools respect the logged-in session (per-user tools later)
  void currentAuth();
}

// ---------------- Averaging tool ----------------
function fmtAvg(n: number): string {
  if (!isFinite(n)) return "—";
  // at most 2 decimals; drop trailing zeros, keep thousands separators
  return (Math.round(n * 100) / 100).toLocaleString("en-US", { maximumFractionDigits: 2 });
}

function mountAveraging(host: HTMLElement) {
  host.innerHTML = `
    <div class="avg-card">
      <div class="avg-grid">
        <div class="avg-left">
          <div class="avg-head">Values</div>
          <div class="avg-rows" id="avg-rows"></div>
          <button class="mini-btn" id="avg-add">+ Add value</button>
        </div>
        <div class="avg-right">
          <div class="avg-res-label">Average</div>
          <div class="avg-res-value" id="avg-value">—</div>
          <div class="avg-res-meta" id="avg-meta">0 values · sum —</div>
        </div>
      </div>
    </div>`;

  const rowsEl = host.querySelector<HTMLElement>("#avg-rows")!;
  const valueEl = host.querySelector<HTMLElement>("#avg-value")!;
  const metaEl = host.querySelector<HTMLElement>("#avg-meta")!;

  const MAX_FONT = 54;
  const MIN_FONT = 22;
  // Shrink the result font until it fits on a single line within its column.
  const fitValue = () => {
    let size = MAX_FONT;
    valueEl.style.fontSize = size + "px";
    while (size > MIN_FONT && valueEl.scrollWidth > valueEl.clientWidth) {
      size -= 1;
      valueEl.style.fontSize = size + "px";
    }
  };

  const recompute = () => {
    const inputs = rowsEl.querySelectorAll<HTMLInputElement>("input");
    let sum = 0;
    let n = 0;
    inputs.forEach((inp) => {
      const raw = inp.value.trim();
      if (raw === "") return;
      const v = parseFloat(raw);
      if (isFinite(v)) {
        sum += v;
        n += 1;
      }
    });
    if (n === 0) {
      valueEl.textContent = "—";
      valueEl.classList.remove("neg");
      metaEl.textContent = `0 values · sum —`;
      fitValue();
      return;
    }
    const avg = sum / n;
    valueEl.textContent = fmtAvg(avg);
    valueEl.classList.toggle("neg", avg < 0);
    metaEl.textContent = `${n} value${n === 1 ? "" : "s"} · sum ${fmtAvg(sum)}`;
    fitValue();
  };

  window.addEventListener("resize", fitValue);

  const updateRemovable = () => {
    const rows = rowsEl.querySelectorAll(".avg-row");
    // keep at least 2 boxes around
    rows.forEach((r) => {
      const btn = r.querySelector<HTMLButtonElement>(".avg-del")!;
      btn.disabled = rows.length <= 2;
    });
  };

  const addRow = (focus = false) => {
    const row = document.createElement("div");
    row.className = "avg-row";
    row.innerHTML = `
      <input type="text" inputmode="decimal" placeholder="0" spellcheck="false" />
      <button class="avg-del" title="Remove" aria-label="Remove">×</button>`;
    const input = row.querySelector<HTMLInputElement>("input")!;
    input.addEventListener("input", recompute);
    // allow only number-ish characters (digits, one dot, leading minus)
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        addRow(true);
        e.preventDefault();
      }
    });
    row.querySelector(".avg-del")!.addEventListener("click", () => {
      if (rowsEl.querySelectorAll(".avg-row").length <= 2) return;
      row.remove();
      updateRemovable();
      recompute();
    });
    rowsEl.appendChild(row);
    updateRemovable();
    if (focus) input.focus();
  };

  // default 3 boxes
  addRow();
  addRow();
  addRow();
  host.querySelector("#avg-add")!.addEventListener("click", () => addRow(true));
  recompute();
}
